import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Checkbox, IconButton } from '@weave-mui/material';
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import {
  EditM as EditIcon,
  TrashM as TrashIcon,
  CopyM as CopyIcon,
  ExportMoveM as MoveIcon,
  PlaybackPlayM as PlayIcon,
  MoreVerticalM as MoreVertIcon,
} from '@weave-mui/icons-weave';

/**
 * TestCaseCard component displays a test case card with checkbox, title, description, and action buttons
 * @param {Object} props - Component props
 * @param {boolean} props.checked - Whether the test case is checked/selected
 * @param {string} props.name - Test case name/title (required)
 * @param {string} [props.description] - Test case description
 * @param {string} props.testcaseid - Test case ID
 * @param {Function} props.onItemSelected - Callback when checkbox is clicked
 * @param {Function} [props.onLabelClick] - Callback when label is clicked
 * @param {boolean} [props.disabled=false] - Whether the test case is disabled
 * @param {boolean} [props.isGitSourceDeleted=false] - Whether the test case was deleted from Git source
 * @param {Array} [props.secondaryActions=[]] - Array of action objects with name, text, and onClick
 */
const TestCaseCard = ({
  checked,
  name,
  description,
  testcaseid,
  onItemSelected,
  onLabelClick,
  disabled = false,
  isGitSourceDeleted = false,
  secondaryActions = [],
}) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [isDescriptionOverflowing, setIsDescriptionOverflowing] =
    useState(false);
  const descriptionRef = useRef(null);

  const onChangeFn = disabled ? () => {} : () => onItemSelected(testcaseid);

  const hasActions = secondaryActions.length > 0;
  const menuOpen = Boolean(menuAnchor);

  useEffect(() => {
    const el = descriptionRef.current;
    if (!el) {
      setIsDescriptionOverflowing(false);
      return undefined;
    }

    const checkOverflow = () => {
      setIsDescriptionOverflowing(
        el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth
      );
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);

    return () => {
      window.removeEventListener('resize', checkOverflow);
    };
  }, [description]);

  // Find specific actions
  const editAction = secondaryActions.find((a) => a.name === 'edit-test-case');
  const deleteAction = secondaryActions.find(
    (a) => a.name === 'delete-test-case'
  );
  const cloneAction = secondaryActions.find(
    (a) => a.name === 'clone-test-case'
  );
  const moveAction = secondaryActions.find((a) => a.name === 'move-test-case');
  const runAction = secondaryActions.find((a) => a.name === 'run-test-case');

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = (event) => {
    if (event) {
      event.stopPropagation();
    }
    setMenuAnchor(null);
  };

  const handleActionClick = (action) => (event) => {
    event.stopPropagation();
    handleMenuClose(event);
    if (action && action.onClick) {
      action.onClick();
    }
  };

  return (
    <Box
      sx={{
        border: '1px solid #ccc',
        borderRadius: '2px',
        padding: '10px',
        backgroundColor: checked ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
        position: 'relative',
        cursor: disabled ? 'not-allowed' : 'default',
        opacity: disabled ? 0.6 : 1,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
      }}
    >
      {/* Checkbox - Top Left */}
      <Checkbox
        checked={checked && !disabled}
        onChange={onChangeFn}
        disabled={disabled}
        size="small"
        sx={{
          position: 'absolute',
          top: '10px',
          left: '10px',
          padding: 0,
        }}
      />

      {/* Three-dot menu - Top Right */}
      {hasActions && (
        <IconButton
          onClick={handleMenuOpen}
          size="small"
          sx={{
            position: 'absolute',
            top: '6px',
            right: '6px',
            padding: '4px',
            color: 'text.primary',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.08)',
            },
          }}
        >
          <MoreVertIcon />
        </IconButton>
      )}

      {/* Content area with padding for checkbox */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
          flex: 1,
          minHeight: 0,
          paddingLeft: '32px',
          paddingRight: hasActions ? '32px' : '0px',
        }}
      >
        {/* Title */}
        <Box
          onClick={onLabelClick}
          sx={{
            cursor: disabled ? 'not-allowed' : 'pointer',
            flexShrink: 0,
            marginLeft: '0px !important',
            paddingLeft: '0px !important',
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 900,
              fontSize: '14px',
              lineHeight: 1.2,
              color: 'text.primary',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              whiteSpace: 'normal',
              textAlign: 'left',
              margin: 0,
              marginLeft: '0px !important',
              padding: 0,
              paddingLeft: '0px !important',
            }}
          >
            {name}
            {isGitSourceDeleted && (
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  marginLeft: '8px',
                  padding: '2px 8px',
                  backgroundColor: 'error.light',
                  color: 'error.main',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: 500,
                }}
                title="This test case was deleted from Git source"
              >
                <span role="img" aria-label="Warning">
                  ⚠️
                </span>{' '}
                Deleted from Git
              </Box>
            )}
          </Typography>
        </Box>

        {/* Description - Limited to 3 lines */}
        {description && (
          <Tooltip
            title={description}
            placement="bottom-start"
            arrow
            disableHoverListener={!isDescriptionOverflowing}
            disableFocusListener={!isDescriptionOverflowing}
            disableTouchListener={!isDescriptionOverflowing}
          >
            <Box
              sx={{
                flex: 1,
              }}
            >
              <Typography
                ref={descriptionRef}
                variant="caption"
                sx={{
                  fontSize: '12px',
                  lineHeight: 1.5,
                  color: 'text.secondary',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  textAlign: 'left',
                }}
              >
                {description}
              </Typography>
            </Box>
          </Tooltip>
        )}
      </Box>

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {editAction && (
          <MenuItem onClick={handleActionClick(editAction)}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              <Typography variant="body2">Edit</Typography>
            </ListItemText>
          </MenuItem>
        )}

        {cloneAction && (
          <MenuItem onClick={handleActionClick(cloneAction)}>
            <ListItemIcon>
              <CopyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              <Typography variant="body2">Clone</Typography>
            </ListItemText>
          </MenuItem>
        )}

        {moveAction && (
          <MenuItem onClick={handleActionClick(moveAction)}>
            <ListItemIcon>
              <MoveIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              <Typography variant="body2">Move</Typography>
            </ListItemText>
          </MenuItem>
        )}

        {runAction && (
          <MenuItem onClick={handleActionClick(runAction)}>
            <ListItemIcon>
              <PlayIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              <Typography variant="body2">Run</Typography>
            </ListItemText>
          </MenuItem>
        )}

        {deleteAction && (
          <MenuItem onClick={handleActionClick(deleteAction)}>
            <ListItemIcon>
              <TrashIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>
              <Typography variant="body2" color="error">
                Delete
              </Typography>
            </ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default TestCaseCard;
